import { Static, Type } from '@sinclair/typebox';
import { LeadQuizSchema, QuizLieuSchema, QuizVerticaleSchema } from './onboarding/lead-quiz.schema';

/**
 * API publique du quiz d'avant-inscription (`/public/lead-quiz`, spec onboarding v4 écrans 1 à
 * 7). Le brouillon vit sur `lead` (`source = 'quiz'`, `quiz` JSON, `preview_token` = token du
 * quiz, gardé 30 jours en cookie côté LP et présent dans l'URL de chaque écran). AUCUNE donnée
 * personnelle avant l'écran 7, sauf l'e-mail de l'écran 2 bis (projet lointain).
 */

/** Valeur de `lead.source` pour un lead né du quiz (lue par le signup → parcours v2). */
export const LEAD_SOURCE_QUIZ = 'quiz';

/** Cookie LP qui garde le token du quiz 30 jours (même appareil). */
export const LEAD_QUIZ_TOKEN_COOKIE = 'propelsia_quiz_token';
export const LEAD_QUIZ_TOKEN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/** Paramètre d'URL du token sur chaque écran du quiz (`/start?t=…`). */
export const LEAD_QUIZ_TOKEN_QUERY_PARAM = 't';
/** Paramètre d'URL de l'écran courant (`/start?t=…&e=3`). */
export const LEAD_QUIZ_STEP_QUERY_PARAM = 'e';
/**
 * Lien d'entrée dans l'app après l'écran 7 (inscription faite sur le site) :
 * `${FRONTEND_URL}/resume?handoff=<jeton>`. Jeton à usage unique, consommé à l'échange, 15 minutes :
 * juste le temps de sortir du navigateur intégré. Expiré ou déjà utilisé → le SMS d'accès et
 * l'e-mail de bienvenue (lien de reprise, réutilisable) prennent le relais.
 */
export const LEAD_QUIZ_HANDOFF_QUERY_PARAM = 'handoff';
export const LEAD_QUIZ_HANDOFF_TTL_MS = 15 * 60_000;
/** Où atterrit l'inscrit du quiz : l'écran 8 (démo) du tunnel obligatoire, jamais le wizard classique. */
export const LEAD_QUIZ_HANDOFF_REDIRECT_PATH = '/onboarding/tunnel';

const HONEYPOT = {
  /** Champ piège : rempli ⇒ bot, la requête est acceptée mais rien n'est écrit. */
  hp: Type.Optional(Type.String({ maxLength: 200 })),
};

const AttributionFields = {
  lp_variant: Type.Optional(Type.String({ maxLength: 64 })),
  referrer_domain: Type.Optional(Type.String({ maxLength: 255 })),
  landing_page: Type.Optional(Type.String({ maxLength: 512 })),
  utm_source: Type.Optional(Type.String({ maxLength: 128 })),
  utm_medium: Type.Optional(Type.String({ maxLength: 128 })),
  utm_campaign: Type.Optional(Type.String({ maxLength: 128 })),
  utm_content: Type.Optional(Type.String({ maxLength: 256 })),
  utm_term: Type.Optional(Type.String({ maxLength: 256 })),
  utm_id: Type.Optional(Type.String({ maxLength: 64 })),
  meta_campaign_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_adset_id: Type.Optional(Type.String({ maxLength: 32 })),
  meta_ad_id: Type.Optional(Type.String({ maxLength: 32 })),
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  gclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  msclkid: Type.Optional(Type.String({ maxLength: 512 })),
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  li_fat_id: Type.Optional(Type.String({ maxLength: 512 })),
  epik: Type.Optional(Type.String({ maxLength: 512 })),
};

/**
 * Signaux de consentement publicitaire du navigateur, même régime que le body du signup : le
 * consentement marketing de la bannière, et les cookies des pixels (`_fbp`, `_fbc`, `_ttp`,
 * `__obref`) SEULEMENT quand il est donné. Rangés sur `qualification.attribution` du lead : ils
 * décident du niveau de détail des conversions serveur (`lead_parked` → Meta `Lead`).
 */
const ConsentSignalFields = {
  tracking_consent: Type.Optional(Type.Boolean()),
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
};
export const LeadQuizConsentSignalsSchema = Type.Object(ConsentSignalFields);
export type LeadQuizConsentSignals = Static<typeof LeadQuizConsentSignalsSchema>;

export const LeadQuizCreateRequestSchema = Type.Object({
  /** Verticale de la pub (`/start?v=`). */
  v: Type.Optional(QuizVerticaleSchema),
  device: Type.Optional(Type.Union([Type.Literal('mobile'), Type.Literal('desktop')])),
  langue: Type.Optional(Type.String({ maxLength: 8 })),
  ...AttributionFields,
  ...ConsentSignalFields,
  ...HONEYPOT,
});
export type LeadQuizCreateRequestObject = Static<typeof LeadQuizCreateRequestSchema>;

/** Écriture par écran : un patch partiel du brouillon (jamais `v`). */
export const LeadQuizPatchRequestSchema = Type.Object({
  patch: Type.Partial(Type.Omit(LeadQuizSchema, ['v'])),
  ...HONEYPOT,
});
export type LeadQuizPatchRequestObject = Static<typeof LeadQuizPatchRequestSchema>;

/** Écran 2 bis « Gardez-moi une place » : e-mail + mois de lancement visé. */
export const LeadQuizParkRequestSchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  /** `YYYY-MM` ; absent = « plus tard » (relance à 3 mois). */
  project_month: Type.Optional(Type.String({ pattern: '^\\d{4}-(0[1-9]|1[0-2])$' })),
  // Relus au moment du « projet lointain » : la bannière a pu être acceptée depuis la création.
  ...ConsentSignalFields,
  ...HONEYPOT,
});
export type LeadQuizParkRequestObject = Static<typeof LeadQuizParkRequestSchema>;

/** Écran 6 : analyse en direct du texte libre (debounce côté client). Rien n'est écrit. */
export const LeadQuizAnalyzeRequestSchema = Type.Object({
  text: Type.String({ maxLength: 1000 }),
});
export type LeadQuizAnalyzeRequestObject = Static<typeof LeadQuizAnalyzeRequestSchema>;

export const LeadQuizAnalyzeResponseSchema = Type.Object({
  prestations: Type.Array(Type.String()),
  lieux: Type.Array(QuizLieuSchema),
  ville: Type.Optional(Type.String()),
  metier: Type.Optional(Type.String()),
  /** Nombre de mots du texte. */
  mots: Type.Number(),
  /** 15 mots ou une prestation repérée : assez pour « Préparer ma démo ». */
  minimum_atteint: Type.Boolean(),
});
export type LeadQuizAnalyzeResponseObject = Static<typeof LeadQuizAnalyzeResponseSchema>;

/**
 * Écran 6 : prestations proposées pour un métier (`GET /public/lead-quiz/prestations`), toutes
 * cochées par défaut côté LP. Résolution serveur : `activity` (slug du référentiel) sinon
 * `metier` (saisie libre rattachée au référentiel par similarité, puis par mots-clés de pack).
 * Rien n'est écrit.
 */
export const LeadQuizPrestationSuggestionSchema = Type.Object({
  nom: Type.String({ maxLength: 120 }),
  prix_eur: Type.Optional(Type.Number({ minimum: 0 })),
  duree_min: Type.Optional(Type.Integer({ minimum: 5 })),
  /** `prix_eur` proposé par le modèle pour un métier précis (pas le prix du pro) : « à confirmer » dans le tunnel. */
  prix_estime: Type.Optional(Type.Boolean()),
});
export type LeadQuizPrestationSuggestionObject = Static<typeof LeadQuizPrestationSuggestionSchema>;

export const LeadQuizPrestationsResponseSchema = Type.Object({
  /** Métier du référentiel retenu (absent si la saisie libre n'a rien rapproché). */
  activity_slug: Type.Optional(Type.String({ maxLength: 120 })),
  activity_label: Type.Optional(Type.String({ maxLength: 120 })),
  prestations: Type.Array(LeadQuizPrestationSuggestionSchema, { maxItems: 12 }),
});
export type LeadQuizPrestationsResponseObject = Static<typeof LeadQuizPrestationsResponseSchema>;

/** Plafond de prestations proposées à l'écran 6 (au-delà, la liste ne se lit plus d'un coup). */
export const LEAD_QUIZ_MAX_SUGGESTED_PRESTATIONS = 12;

export const LeadQuizStatusSchema = Type.Union([
  /** Quiz en cours. */
  Type.Literal('draft'),
  /** Projet lointain (écran 2 bis validé). */
  Type.Literal('parked'),
  /** Le compte existe : tout retour passe par l'app. */
  Type.Literal('converted'),
]);
export type LeadQuizStatus = Static<typeof LeadQuizStatusSchema>;

export const LeadQuizResponseSchema = Type.Object({
  token: Type.String(),
  status: LeadQuizStatusSchema,
  quiz: LeadQuizSchema,
  /** La démo (preview from_scratch) a été générée. */
  demo_ready: Type.Boolean(),
  /** Génération demandée (en cours ou faite). */
  demo_requested: Type.Boolean(),
  demo_niveau: Type.Optional(Type.Union([Type.Literal('minimal'), Type.Literal('complet')])),
  /** Cible de la passerelle : `/register?pt=<token>` sur l'app. */
  register_url: Type.String(),
});
export type LeadQuizResponseObject = Static<typeof LeadQuizResponseSchema>;

/** Nombre de mots minimal du texte libre pour « Préparer ma démo » (écran 6). */
export const LEAD_QUIZ_MIN_WORDS = 15;

export const countWords = (text: string): number =>
  text
    .trim()
    .split(/\s+/)
    .filter((word): boolean => word.length > 0).length;
